import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { motion } from 'framer-motion'
import { useLanguage } from '../contexts/LanguageContext'
import ScrollArrow from '../components/ScrollArrow'
import GlareCard from '../components/ui/GlareCard'
import ProgressiveImage from '../components/ui/ProgressiveImage'
import { textReveal, staggerContainer } from '../animations/variants'


const Home = () => {
  const { t } = useLanguage()

  return (
    <div className="bg-black">
      {/* ===== Hero Section - Full Screen ===== */}
      <section className="section-fullscreen">
        {/* Background Image */}
        <div className="section-bg">
          <ProgressiveImage
            src="/images/hero-home.jpg"
            placeholder="/images/placeholders/hero-home-placeholder.jpg"
            alt="Automotive"
          />
          <div className="section-overlay" />
          <div className="section-overlay-left" />
        </div>

        {/* Content */}
        <div className="section-content">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.div variants={textReveal} className="section-label">DIGITAL ENGINEERING PLATFORM</motion.div>
            <motion.h1 variants={textReveal} className="section-heading text-glow">
              The Soul<br />
              <span className="text-gray-500 font-light">of</span> Mobility
            </motion.h1>
            <motion.p variants={textReveal} className="section-description">
              {t('home.hero.detail')}
            </motion.p>
            <motion.div variants={textReveal}>
              <Link to="/products" className="section-cta group">
                <span>{t('home.hero.cta1')}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>

        <ScrollArrow />
      </section>

      {/* ===== One Soul Section - Full Screen ===== */}
      <section className="section-fullscreen">
        <div className="section-bg">
          <ProgressiveImage
            src="/images/section-soul.jpg"
            placeholder="/images/placeholders/section-soul-placeholder.jpg"
            alt="Technology"
          />
          <div className="section-overlay" />
          <div className="section-overlay-left" />
        </div>

        <div className="section-content">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="max-w-2xl"
          >
            <motion.div variants={textReveal} className="section-label">CORE PLATFORM</motion.div>
            <motion.h2 variants={textReveal} className="section-heading">
              One Soul
            </motion.h2>
            <motion.p variants={textReveal} className="section-description">
              {t('products.onesoul.subtitle')}
            </motion.p>
            <motion.div variants={textReveal}>
              <Link to="/products#one-soul" className="section-cta group">
                <span>{t('nav.learnMore')}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>

        <ScrollArrow />
      </section>

      {/* ===== One Body Section - Full Screen ===== */}
      <section className="section-fullscreen">
        <div className="section-bg">
          <ProgressiveImage
            src="/images/section-body.jpg"
            placeholder="/images/placeholders/section-body-placeholder.jpg"
            alt="Vehicle Design"
          />
          <div className="section-overlay" />
          <div className="section-overlay-left" />
        </div>

        <div className="section-content">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="max-w-2xl"
          >
            <motion.div variants={textReveal} className="section-label">DEVELOPMENT PLATFORM</motion.div>
            <motion.h2 variants={textReveal} className="section-heading">
              One Body
            </motion.h2>
            <motion.p variants={textReveal} className="section-description">
              {t('products.onebody.subtitle')}
            </motion.p>
            <motion.div variants={textReveal}>
              <Link to="/products#one-body" className="section-cta group">
                <span>{t('nav.learnMore')}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>

        <ScrollArrow />
      </section>

      {/* ===== Features Section - 网格纹理背景 ===== */}
      <section className="relative z-10 py-32 px-6 md:px-20 bg-grid-texture">
        {/* 顶部渐变过渡 */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black to-transparent pointer-events-none" />
        {/* 底部渐变过渡 */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent pointer-events-none" />

        <div className="relative max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <motion.div variants={textReveal} className="section-label justify-center flex mb-6">CAPABILITIES</motion.div>
            <motion.h2 variants={textReveal} className="text-4xl md:text-5xl font-bold text-white uppercase tracking-[-0.02em]">
              {t('home.feature.title')}
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {[
              { icon: 'memory', title: t('home.feature.simulation.title'), desc: t('home.feature.simulation.desc') },
              { icon: 'precision_manufacturing', title: t('home.feature.collaboration.title'), desc: t('home.feature.collaboration.desc') },
              { icon: 'auto_mode', title: t('home.feature.ai.title'), desc: t('home.feature.ai.desc') },
              { icon: 'cloud', title: t('home.feature.cloud.title'), desc: t('home.feature.cloud.desc') },
              { icon: 'directions_car', title: t('home.feature.vehicle.title'), desc: t('home.feature.vehicle.desc') },
              { icon: 'verified', title: t('home.feature.industrial.title'), desc: t('home.feature.industrial.desc') }
            ].map((feature, index) => (
              <GlareCard key={index}>
                <span className="material-symbols-outlined text-3xl text-white/30 mb-4">{feature.icon}</span>
                <h3 className="section-title mb-4">{feature.title}</h3>
                <p className="section-desc">{feature.desc}</p>
              </GlareCard>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== CTA Section - Full Screen ===== */}
      <section className="section-fullscreen">
        <div className="section-bg">
          <ProgressiveImage
            src="/images/section-cta.jpg"
            placeholder="/images/placeholders/section-cta-placeholder.jpg"
            alt="Future"
          />
          <div className="section-overlay" />
        </div>

        <div className="relative z-10 h-full min-h-screen flex flex-col items-center justify-center px-6 md:px-20 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="max-w-3xl"
          >
            <motion.div variants={textReveal} className="section-label justify-center flex mb-6">GET STARTED</motion.div>
            <motion.h2 variants={textReveal} className="section-heading mb-6">
              {t('home.cta.title')}
            </motion.h2>
            <motion.p variants={textReveal} className="text-gray-400 text-base md:text-lg font-mono mb-10 max-w-2xl mx-auto">
              {t('home.cta.subtitle')}
            </motion.p>
            <motion.div variants={textReveal} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/products" className="section-cta group">
                <span>{t('home.cta.cta1')}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/contact" className="section-cta group border-white/10 hover:border-white/30 hover:bg-transparent hover:text-white">
                <span>{t('home.cta.cta2')}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Home
